// ═══════════════════════════════════════════════════════════════
// PixAlloy — Alloyhead WasmEngineAdapter
// ═══════════════════════════════════════════════════════════════
// Wraps the PixAlloy JS engine (mkEngine) and compiled Wasm kernels
// into the WasmEngineAdapter interface required by HostBridge.
//
// Usage:
//   const { adapter, engine, wasmBridge, sideband } = await createPixAlloyAdapter(1200, 800);
//   bridge.initWithWasm(canvas, adapter);
//
// The adapter:
//   1. Creates the PixAlloy engine (mkEngine)
//   2. Loads and initializes the Wasm kernel binary
//   3. Wires the bridge (setBridge) so filters route through Wasm
//   4. Translates Alloyhead's pushParams/pushCommand/tick/render
//      into PixAlloy's engine API
//
// Sideband API:
//   Commands with complex payloads (point arrays, strings, nested objects)
//   that don't fit in the ring buffer's 28-byte binary slots. The React UI
//   calls these directly instead of pushing to the ring.
// ═══════════════════════════════════════════════════════════════

import type { WasmEngineAdapter, LayerSyncEntry } from '@alloyhead/core';
import {
  RB_DIRTY, RB_HAS_SELECTION, RB_SEL_X, RB_SEL_Y, RB_SEL_W, RB_SEL_H,
  RB_HAS_TRANSFORM, RB_TFM_DATA, RB_STROKE_ON, RB_LAYER_COUNT, RB_ACTIVE_LAYER,
  KernelRegistry,
} from '@alloyhead/core';
import { mkEngine, mkL } from '../pixalloy-src/engine/index.js';
import { WasmKernelBridge } from '@alloyhead/core';
import { PIXALLOY_WASM_B64 } from '../pixalloy-src/wasm/kernels-wasm-b64.js';
import { registerPixAlloyKernels } from '../pixalloy-src/engine/kernel-integration.js';
import { C, BLEND_MAP } from '../pixalloy-src/engine/constants.js';

// Reverse map: blend mode index → canvas compositeOperation string
const BLEND_BY_IDX = Object.values(BLEND_MAP);
// Forward map: UI label → index (for encoding blend mode as Uint32)
const BLEND_LABEL_TO_IDX: Record<string, number> = {};
Object.keys(BLEND_MAP).forEach((label, i) => { BLEND_LABEL_TO_IDX[label] = i; });

// Range string → Uint32 for hue/sat filter
const RANGE_MAP: Record<string, number> = { master: 0, reds: 1, yellows: 2, greens: 3, cyans: 4, blues: 5, magentas: 6 };
const RANGE_BY_IDX = Object.keys(RANGE_MAP);

export { BLEND_LABEL_TO_IDX, RANGE_MAP };

export async function createPixAlloyAdapter(
  width: number,
  height: number,
): Promise<{ adapter: WasmEngineAdapter; engine: ReturnType<typeof mkEngine>; wasmBridge: WasmKernelBridge; sideband: ReturnType<typeof buildSideband> }> {

  // ── 1. Create the PixAlloy engine ──
  const engine = mkEngine(width, height);

  // Thumbnail throttle: regenerate at ~4Hz instead of every dirty frame.
  // Between intervals, layer sync omits thumbnails and the UI keeps the last ones.
  let lastThumbGenTime = 0;
  // Reusable OffscreenCanvas per layer slot — avoids allocating new ones each sync.
  const thumbCanvasCache = new Map<number, OffscreenCanvas>();

  // ── 2. Initialize Wasm kernels via framework WasmKernelBridge + KernelRegistry ──
  // Uses the framework's WasmKernelBridge (with arena allocator, SIMD alignment)
  // instead of PixAlloy's standalone WasmBridge. The embedded 6.6KB binary is
  // loaded via initFromBase64.
  const wasmBridge = new WasmKernelBridge();
  await wasmBridge.initFromBase64(PIXALLOY_WASM_B64);

  // Create a KernelRegistry and register all 9 PixAlloy kernels
  // with both Wasm exports and JS fallbacks. The registry handles
  // dispatch: tries Wasm first, falls back to JS automatically.
  const kernelRegistry = new KernelRegistry();
  registerPixAlloyKernels(kernelRegistry, wasmBridge);

  // Inject the registry into the engine — replaces the old setBridge(wasmBridge) pattern.
  // engine.setBridge accepts anything with a .run() method (KernelRegistry) or ._wasmAvailable (legacy).
  engine.setBridge(kernelRegistry);

  console.log('[PixAlloy] Adapter created — Kernels:', kernelRegistry.list().length, '— Wasm:', wasmBridge.available ? 'active' : 'JS fallback');

  // ── 3. Build the adapter ──
  const adapter: WasmEngineAdapter = {

    pushParams(readParam) {
      // Translate Alloyhead ParamReader → PixAlloy engine.pushParams
      engine.pushParams(readParam);
    },

    pushCommand(cmdType, payload) {
      // Translate Alloyhead command slot → PixAlloy engine.pushCommand
      // The payload is a DataView over the 28-byte command payload region.
      // We need to reconstruct the PixAlloy payload object from the binary data.
      const pl = decodePayload(cmdType, payload);
      engine.pushCommand({ type: cmdType, payload: pl });
    },

    tick(_dt) {
      // PixAlloy's tick handles brush painting and continuous operations
      engine.tick();
      return 0; // EngineStatus.OK
    },

    render(ctx, w, h) {
      // PixAlloy's engine renders the full composited frame
      engine.render(ctx, w, h);
    },

    resize(w, h) {
      // VIEWPORT resize only — updates the engine's render target dimensions.
      // Does NOT resample layer pixels (that's what the IMG_RESIZE command does).
      //
      // The HostBridge calls this when the canvas element dimensions change
      // (e.g., browser window resize). This should NOT trigger a destructive
      // document resize — that would erase pixel data every time the window resizes.
      //
      // To resize the document (resample all layers), push IMG_RESIZE through
      // the ring buffer or sideband: bridge.pushU32Pair(C.IMG_RESIZE, w, h).
      //
      // PixAlloy's render() already handles viewport != document size by
      // scaling the composite output to fit the canvas, so no engine command
      // is needed here.
    },

    writeReadback(view: DataView) {
      // Write engine state into the readback SAB for the overlay to read.
      // In single-thread mode this is synchronous (same memory).
      // In worker mode the worker does the same writes — main thread reads via Atomics.
      const s = engine.s;

      // Dirty flag — the overlay checks this to trigger React re-renders
      view.setUint32(RB_DIRTY, s.dirty ? 1 : 0, true);
      s.dirty = false; // Clear after writing — prevents repeated dirty signals

      // Selection bounds
      if (s.sel) {
        view.setUint32(RB_HAS_SELECTION, 1, true);
        view.setFloat32(RB_SEL_X, s.sel.x ?? 0, true);
        view.setFloat32(RB_SEL_Y, s.sel.y ?? 0, true);
        view.setFloat32(RB_SEL_W, s.sel.w ?? 0, true);
        view.setFloat32(RB_SEL_H, s.sel.h ?? 0, true);
      } else {
        view.setUint32(RB_HAS_SELECTION, 0, true);
      }

      // Transform state
      if (s.tfm) {
        view.setUint32(RB_HAS_TRANSFORM, 1, true);
        // Pack transform fields into the 64-byte region at RB_TFM_DATA:
        // [0-3]  f32 x
        // [4-7]  f32 y
        // [8-11] f32 w
        // [12-15] f32 h
        // [16-19] f32 rotation
        // [20-23] f32 scaleX
        // [24-27] f32 scaleY
        // [28-31] u32 mode flags (selOnly)
        // [32-47] f32×4 corner handles (cx, cy reserved)
        const base = RB_TFM_DATA;
        view.setFloat32(base,      s.tfm.x ?? 0, true);
        view.setFloat32(base + 4,  s.tfm.y ?? 0, true);
        view.setFloat32(base + 8,  s.tfm.w ?? 0, true);
        view.setFloat32(base + 12, s.tfm.h ?? 0, true);
        view.setFloat32(base + 16, s.tfm.rot ?? 0, true);
        view.setFloat32(base + 20, s.tfm.sx ?? 1, true);
        view.setFloat32(base + 24, s.tfm.sy ?? 1, true);
        view.setUint32(base + 28, s.tfm.selOnly ? 1 : 0, true);
      } else {
        view.setUint32(RB_HAS_TRANSFORM, 0, true);
      }

      // Stroke on/off (suppresses histogram during strokes)
      view.setUint32(RB_STROKE_ON, s.strokeOn ? 1 : 0, true);

      // Layer count + active layer (for overlay reference)
      view.setUint32(RB_LAYER_COUNT, s.layers.length, true);
      view.setUint32(RB_ACTIVE_LAYER, s.act ?? 0, true);
    },

    getLayerSnapshot(): LayerSyncEntry[] {
      // Throttle thumbnail generation: only regenerate thumbnails at ~4Hz (250ms).
      // Between intervals, reuse previously cached thumbnails (still valid ImageBitmaps).
      const now = performance.now();
      const shouldRegenThumbs = now - lastThumbGenTime > 250;
      if (shouldRegenThumbs) lastThumbGenTime = now;

      return engine.s.layers.map((l: any, i: number) => {
        const entry: LayerSyncEntry = {
          name: l.name,
          vis: l.vis,
          op: l.op,
          blend: l.blend,
          type: l.type || 'raster',
          maskEnabled: l.maskEnabled,
          adjType: l.adjType || null,
        };
        // Generate 30×30 thumbnail for layer panel (skip for adjustment/group layers)
        if (l.c && l.type !== 'adjustment' && l.type !== 'group') {
          if (shouldRegenThumbs) {
            try {
              const thumbSize = 30;
              // Reuse a cached OffscreenCanvas per layer slot to avoid allocation
              let tc = thumbCanvasCache.get(i);
              if (!tc) {
                tc = new OffscreenCanvas(thumbSize, thumbSize);
                thumbCanvasCache.set(i, tc);
              }
              const tx = tc.getContext('2d')!;
              tx.clearRect(0, 0, thumbSize, thumbSize);
              tx.drawImage(l.c, 0, 0, thumbSize, thumbSize);
              entry.thumbnail = tc.transferToImageBitmap();
            } catch { /* skip thumbnail on error */ }
          }
          // If not regenerating, omit thumbnail — the UI keeps the last one
        }
        return entry;
      });
    },

    getSelectionMask() {
      const s = engine.s;
      if (!s.sel) return null;
      return {
        mask: s.sel.data || s.sel.mask || new Uint8Array(0),
        width: s.w,
        height: s.h,
        x: s.sel.x ?? 0,
        y: s.sel.y ?? 0,
        w: s.sel.w ?? s.w,
        h: s.sel.h ?? s.h,
        gen: s._selGen || 0,
      };
    },

    getTransformData() {
      const s = engine.s;
      if (!s.tfm || !s.tfm.origData) return null;
      // Create an OffscreenCanvas from the transform's origData (ImageData)
      const od = s.tfm.origData;
      const oc = new OffscreenCanvas(od.width, od.height);
      const octx = oc.getContext('2d')!;
      octx.putImageData(od, 0, 0);
      return {
        canvas: oc,
        x: s.tfm.x ?? 0,
        y: s.tfm.y ?? 0,
        w: s.tfm.w ?? od.width,
        h: s.tfm.h ?? od.height,
        rot: s.tfm.rot ?? 0,
        sx: s.tfm.sx ?? 1,
        sy: s.tfm.sy ?? 1,
        selOnly: !!s.tfm.selOnly,
      };
    },

    composite() {
      return engine.composite();
    },

    pushSidebandCommand(type: number, payload: Record<string, unknown>) {
      engine.pushCommand({ type, payload });
    },

    onDimsChanged(callback: (w: number, h: number) => void) {
      engine.s.onDimsChanged = callback;
    },
  };

  // ── 4. Build sideband API ──
  const sideband = buildSideband(engine);

  return { adapter, engine, wasmBridge, sideband };
}

// ═══════════════════════════════════════════════════════════════
// Sideband API — commands with complex payloads that bypass the ring
// ═══════════════════════════════════════════════════════════════
// In single-thread mode these call the engine directly.
// In worker mode they would go via MessageChannel + structured clone.

function buildSideband(engine: ReturnType<typeof mkEngine>) {
  return {
    /** Lasso selection — variable-length point array */
    selLasso(points: Array<{x:number,y:number}>, add?: boolean, sub?: boolean) {
      engine.pushCommand({ type: C.SEL_LASSO, payload: { points, add, sub } });
    },
    /** Polygon lasso — variable-length point array */
    selPoly(points: Array<{x:number,y:number}>, add?: boolean, sub?: boolean) {
      engine.pushCommand({ type: C.SEL_POLY, payload: { points, add, sub } });
    },
    /** Add text layer — string + font config */
    textAdd(text: string, font: string, size: number, weight: string, color: string, align: string, x: number, y: number) {
      engine.pushCommand({ type: C.TEXT_ADD, payload: { text, font, size, weight, color, align, x, y } });
    },
    /** Update text — partial override */
    textUpdate(props: Record<string, unknown>) {
      engine.pushCommand({ type: C.TEXT_UPDATE, payload: props });
    },
    /** Layer effects — nested shadow/bevel/outline objects */
    fxUpdate(layerIdx: number, fx: Record<string, unknown>) {
      engine.pushCommand({ type: C.FX_UPDATE, payload: { layerIdx, fx } });
    },
    /** Add adjustment layer — type string + params object */
    adjLayerAdd(adjType: string, params: Record<string, number>) {
      engine.pushCommand({ type: C.ADJ_LAYER_ADD, payload: { adjType, params } });
    },
    /** Update adjustment layer params */
    adjLayerUpdate(idx: number, params: Record<string, number>) {
      engine.pushCommand({ type: C.ADJ_LAYER_UPDATE, payload: { idx, params } });
    },
    /** Rename layer — string payload */
    layerRename(idx: number, name: string) {
      engine.pushCommand({ type: C.LAYER_RENAME, payload: { idx, name } });
    },
    /** Convert pen path to selection — point array with bezier handles */
    penToSel(points: Array<Record<string, unknown>>, closed?: boolean, add?: boolean, sub?: boolean) {
      engine.pushCommand({ type: C.PEN_TO_SEL, payload: { points, closed, add, sub } });
    },
    /** Stroke pen path — point array + color */
    penStroke(points: Array<Record<string, unknown>>, r: number, g: number, b: number, size: number, opacity: number, closed?: boolean) {
      engine.pushCommand({ type: C.PEN_STROKE, payload: { points, r, g, b, size, opacity, closed } });
    },
    /** Fill pen path — point array + color */
    penFill(points: Array<Record<string, unknown>>, r: number, g: number, b: number, opacity: number, closed?: boolean) {
      engine.pushCommand({ type: C.PEN_FILL, payload: { points, r, g, b, opacity, closed } });
    },
    /** Add a new vector layer with PathList data and styling */
    vectorLayerAdd(paths: Array<Record<string, unknown>>, style?: Record<string, unknown>, name?: string) {
      engine.pushCommand({ type: C.VECTOR_LAYER_ADD, payload: { paths, style, name } });
    },
    /** Update an existing vector layer's paths, style, or name */
    vectorLayerUpdate(idx: number, paths?: Array<Record<string, unknown>>, style?: Record<string, unknown>, name?: string) {
      engine.pushCommand({ type: C.VECTOR_LAYER_UPDATE, payload: { idx, paths, style, name } });
    },
    /** Fill with gradient stops */
    fillGradient(x: number, y: number, x2: number, y2: number, mode: string, stops: Array<{pos:number,color:string}>, opacity: number) {
      engine.pushCommand({ type: C.FILL, payload: { x, y, x2, y2, mode, stops, opacity, r:0, g:0, b:0, r2:0, g2:0, b2:0 } });
    },
    /** Fill with solid/pattern — complex because it can carry tolerance, mode, secondary colors */
    fillComplex(payload: Record<string, unknown>) {
      engine.pushCommand({ type: C.FILL, payload });
    },
    /** Levels — carries multiple numeric params */
    levels(inBlack: number, inWhite: number, gamma: number, outBlack: number, outWhite: number) {
      engine.pushCommand({ type: C.LEVELS, payload: { inBlack, inWhite, gamma, outBlack, outWhite } });
    },
    /** Color balance — 9 params + preserveLum flag */
    colorBalance(params: Record<string, number | boolean>) {
      engine.pushCommand({ type: C.COLOR_BALANCE, payload: params });
    },
    /** Hue/Saturation filter — carries range string */
    hueSatFilter(hue: number, saturation: number, lightness: number, range: string) {
      engine.pushCommand({ type: C.HUESAT_FILTER, payload: { hue, saturation, lightness, range } });
    },
    /** Paste with position */
    paste(x: number, y: number) {
      engine.pushCommand({ type: C.PASTE, payload: { x, y } });
    },
    /** Duplicate layer */
    duplicateLayer(idx: number) {
      engine.pushCommand({ type: C.DUPLICATE_LAYER, payload: { idx } });
    },
    /** Visibility toggle for a specific layer */
    vis(idx: number) {
      engine.pushCommand({ type: C.VIS, payload: { idx } });
    },
    /** Layer opacity */
    layerOp(op: number, idx?: number) {
      engine.pushCommand({ type: C.LAYER_OP, payload: { op, idx } });
    },
    /** Layer blend mode (by label string) */
    layerBlend(blend: string, idx?: number) {
      engine.pushCommand({ type: C.LAYER_BLEND, payload: { blend, idx } });
    },
    /** Mask toggle for specific layer */
    maskToggle(idx: number) {
      engine.pushCommand({ type: C.MASK_TOGGLE, payload: { idx } });
    },
    /** Mask link toggle for specific layer */
    maskLink(idx: number) {
      engine.pushCommand({ type: C.MASK_LINK, payload: { idx } });
    },
    /** Group toggle for specific layer */
    groupToggle(idx: number) {
      engine.pushCommand({ type: C.GROUP_TOGGLE, payload: { idx } });
    },
    /** Layer reorder */
    layerReorder(from: number, to: number) {
      engine.pushCommand({ type: C.LAYER_REORDER, payload: { from, to } });
    },
    /** History jump */
    histJump(idx: number) {
      engine.pushCommand({ type: C.HIST_JUMP, payload: { idx } });
    },
    /** Selection from alpha channel */
    selFromAlpha(idx: number, mode?: string) {
      engine.pushCommand({ type: C.SEL_FROM_ALPHA, payload: { idx, mode: mode || 'replace' } });
    },
    /** Fill color with label */
    fillColor(r: number, g: number, b: number, label?: string) {
      engine.pushCommand({ type: C.FILL_COLOR, payload: { r, g, b, label } });
    },
    /** Magic wand with add/sub modifiers */
    selWand(x: number, y: number, tolerance: number, add?: boolean, sub?: boolean) {
      engine.pushCommand({ type: C.SEL_WAND, payload: { x, y, tolerance, add, sub } });
    },
    /** Rubber stamp source set */
    rubberStampSet(x: number, y: number) {
      engine.pushCommand({ type: C.RUBBER_STAMP_SET, payload: { x, y } });
    },
    /** Direct engine access — for anything not covered above */
    pushRaw(type: number, payload: Record<string, unknown>) {
      engine.pushCommand({ type, payload });
    },
  };
}

export type PixAlloySideband = ReturnType<typeof buildSideband>;

// ═══════════════════════════════════════════════════════════════
// Payload Decoder — converts binary DataView to PixAlloy payload objects
// ═══════════════════════════════════════════════════════════════
// The Alloyhead ring buffer stores commands as:
//   [u16 type][u16 flags][28 bytes payload]
// We decode the 28-byte payload based on the command type.
//
// NOTE: Only commands with simple numeric payloads can go through the ring.
// Commands with strings, point arrays, or nested objects MUST use the
// sideband API instead.

function decodePayload(cmdType: number, view: DataView): Record<string, unknown> {
  const pl: Record<string, unknown> = {};

  switch (cmdType) {
    case C.FILTER:
      pl.id = view.getUint32(0, true);
      break;

    case C.SEL_RECT:
    case C.SEL_ELLIPSE:
    case C.CROP:
      pl.x = view.getFloat32(0, true);
      pl.y = view.getFloat32(4, true);
      pl.w = view.getFloat32(8, true);
      pl.h = view.getFloat32(12, true);
      break;

    case C.SEL_WAND:
      pl.x = view.getFloat32(0, true);
      pl.y = view.getFloat32(4, true);
      pl.tolerance = view.getFloat32(8, true);
      break;

    case C.SEL_FEATHER:
      pl.radius = view.getFloat32(0, true);
      break;

    case C.MOVE:
      pl.dx = view.getFloat32(0, true);
      pl.dy = view.getFloat32(4, true);
      break;

    case C.FILL:
      pl.x = view.getFloat32(0, true);
      pl.y = view.getFloat32(4, true);
      break;

    case C.FILL_COLOR:
      pl.r = view.getUint32(0, true);
      pl.g = view.getUint32(4, true);
      pl.b = view.getUint32(8, true);
      break;

    case C.IMG_RESIZE:
    case C.CANVAS_SIZE:
      pl.w = view.getUint32(0, true);
      pl.h = view.getUint32(4, true);
      break;

    case C.GAUSS_BLUR:
      pl.radius = view.getFloat32(0, true);
      break;

    case C.RUBBER_STAMP_SET:
      pl.x = view.getFloat32(0, true);
      pl.y = view.getFloat32(4, true);
      break;

    case C.LAYER_OP:
      pl.op = view.getFloat32(0, true);
      break;

    case C.LAYER_BLEND: {
      const idx = view.getUint32(0, true);
      // Decode blend index back to UI label for the engine
      const labels = Object.keys(BLEND_MAP);
      pl.blend = labels[idx] || 'Normal';
      break;
    }

    case C.LAYER_REORDER:
      pl.from = view.getUint32(0, true);
      pl.to = view.getUint32(4, true);
      break;

    case C.HIST_JUMP:
      pl.idx = view.getUint32(0, true);
      break;

    case C.HUESAT_FILTER:
      pl.hue = view.getFloat32(0, true);
      pl.saturation = view.getFloat32(4, true);
      pl.lightness = view.getFloat32(8, true);
      pl.range = RANGE_BY_IDX[view.getUint32(12, true)] || 'master';
      break;

    case C.SEL_FROM_ALPHA:
      pl.idx = view.getUint32(0, true);
      pl.mode = view.getUint32(4, true) === 0 ? 'replace' : 'add';
      break;

    // Commands with no payload: UNDO, REDO, LAYER_ADD, STROKE_ON/OFF,
    // SEL_CLR, SEL_ALL, SEL_INV, TFM_BEGIN/APPLY/CANCEL/SEL_BEGIN/SEL_APPLY,
    // COPY, CUT, FLATTEN, MASK_ADD/DEL, MERGE_DOWN, IMG_FLIP_H/V,
    // IMG_ROT90/180, GROUP_ADD, CONTENT_FILL, GROUP_MOVE_IN/OUT, ZOOM_FIT, etc.
    default:
      break;
  }

  return pl;
}
